import { useLocale } from 'next-intl';
import { ComponentProps } from 'react';
import { useStorefrontItemsIndex } from '@/entities/product/lib/useStorefrontMenu';
import { ProductCardContext, ProductCardImage, ProductCardName } from '@/entities/product/ui/product-card';
import {
    ProductCardAddToCartButton,
    Icon as ProductCardAddToCartButtonIcon,
    Text as ProductCardAddToCartButtonText,
} from '@/entities/product/ui/product-card/product-card-add-to-cart-button';
import { ProductCardPrice } from '@/entities/product/ui/product-card/product-card-price';
import { useGetPageUrl } from '@/schema/useGetPageUrl';
import { localeToLang } from '@/shared/i18n/localeToLang';
import { applyParentFallbacks } from '@/shared/lib/apply-parent-fallbacks';
import { cn } from '@/shared/lib/cn';
import { EventService } from '@/shared/lib/event-service';
import { getMainAsset } from '@/shared/lib/getMainAsset';
import { Card } from '@/shared/ui/card';
import { Icon } from '@/shared/ui/icon';
import { Layout } from '@/shared/ui/layout';
import { LinkBox } from '@/shared/ui/link-box';
import { useModalContext } from '@/shared/ui/modal/modal-context';
import { prefabsStore } from '@/shared/ui/modal/prefabs-store';
import './favorite-items-modal.scss';

type Props = ComponentProps<typeof Layout> & {
    itemId: string;
};
export const FavoriteItemsModalItem = ({ itemId, className, media, ...props }: Props) => {
    const locale = localeToLang(useLocale());
    const items = useStorefrontItemsIndex();
    const getPageUrl = useGetPageUrl();
    const { onClose } = useModalContext();

    const resolved = (() => {
        for (const entry of Object.values(items)) {
            const type = entry.placement.type;
            if (type !== 'categoryItem' && type !== 'variant') continue;
            if (entry.body.id !== itemId) continue;

            if (type === 'variant') {
                if (!entry.placement.parentHash) return;
                const parent = items[entry.placement.parentHash];
                if (!parent || parent.placement.type !== 'categoryItem') return;
                return {
                    entry: {
                        ...entry,
                        body: {
                            ...applyParentFallbacks(entry.body, parent.body),
                            name: parent.body.name,
                        },
                    },
                    routeSlug: parent.placement.slug,
                    productHash: parent.hash,
                    variantHash: entry.hash,
                    variantName: entry.body.name as Record<string, string>,
                };
            }
            return {
                entry,
                routeSlug: entry.placement.slug,
                productHash: entry.hash,
                variantHash: undefined,
                variantName: undefined,
            };
        }
    })();

    if (!resolved || !resolved.routeSlug?.[locale]) {
        return null;
    }

    const { entry: data, routeSlug, productHash, variantHash, variantName } = resolved;
    const variantLabel = variantName?.[locale];
    const hasImages = !!data.body.assets?.length;
    const detailHref = (() => {
        const url = getPageUrl('product-detail', {
            PRODUCT_SLUG: `${routeSlug[locale]}-${productHash}`,
        });
        return variantHash ? `${url}?variant=${variantHash}` : url;
    })();

    const contextValue: ComponentProps<typeof ProductCardContext.Provider>['value'] = {
        id: data.body.id || data.hash,
        name: data.body.name,
        imageUrl: getMainAsset(data.body.assets)?.fullUrl ?? undefined,
        priceValues: [],
        slug: data.placement.slug?.[locale] ?? '',
        hash: data.hash ?? '',
        setSelectedVariantId: () => {},
    };

    return (
        <ProductCardContext.Provider value={contextValue}>
            <Layout
                element={Card}
                shadow
                className={cn('favorite-items-modal__item', className)}
                media={media}
                {...props}
            >
                {hasImages && (
                    <div className="favorite-items-modal__item-image">
                        <ProductCardImage media={{ initial: { minHeight: '8rem' } }} />
                    </div>
                )}
                <div className="favorite-items-modal__item-content">
                    <div className="favorite-items-modal__item-header">
                        <div className="favorite-items-modal__item-info">
                            {prefabsStore.getProductDetail()?.openMode === 'modal' ? (
                                <button
                                    type="button"
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        padding: 0,
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                    }}
                                    onClick={() => {
                                        onClose();
                                        EventService.trigger('product:addons-menu:view-detail', {
                                            productHash,
                                            initialVariant: variantHash,
                                        });
                                    }}
                                >
                                    <ProductCardName />
                                </button>
                            ) : (
                                <LinkBox
                                    href={detailHref}
                                    onClick={() => {
                                        onClose();
                                    }}
                                >
                                    <ProductCardName />
                                </LinkBox>
                            )}
                            {variantLabel && (
                                <span className="favorite-items-modal__item-variant">{variantLabel}</span>
                            )}
                        </div>
                        <button
                            className="favorite-items-modal__item-favorite-button"
                            onClick={() =>
                                EventService.trigger('account:product:remove-from-favorite', { id: itemId })
                            }
                        >
                            <Icon name="heart-fill" />
                        </button>
                    </div>
                    <Layout
                        media={{
                            initial: {
                                display: 'flex',
                                ...(hasImages
                                    ? {
                                          flexDirection: 'column',
                                      }
                                    : {
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                      }),
                                gap: '0.5rem',
                            },
                        }}
                    >
                        <ProductCardPrice />
                        {/*<ProductCardOriginalPrice priceValues={[{ amount: 0, currency: 'CZK' }]} />*/}
                        <ProductCardAddToCartButton
                            media={{ initial: { alignSelf: 'end', maxWidth: 190, width: '100%' } }}
                        >
                            <ProductCardAddToCartButtonIcon />
                            <ProductCardAddToCartButtonText
                                media={{
                                    initial: { display: 'none' },
                                    '(min-width: 410px)': { display: 'inline' },
                                }}
                            />
                        </ProductCardAddToCartButton>
                    </Layout>
                </div>
            </Layout>
        </ProductCardContext.Provider>
    );
};
